import React, { useEffect, useMemo, useState } from "react";
import { addDays, addYears, format, startOfDay, startOfWeek } from "date-fns";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "../../../style/block.scss";
import "../../../style/feature.scss";
import "../../../style/buttons.scss";
import useSchedule from "./hooks/useSchedule";
import useAdditionalHooks from "./hooks/useAdditionalHooks";
import { buildScheduleFilters } from "./scheduleOptions";
import useFitnessWeekView, { exerciseHourKey } from "./useFitnessWeekView";
import SingleExercise from "./single-exercise";
import ExerciseFltersModal from "./exerciseBar/ExerciseFilters/ExerciseFltersModal";
import { State } from "../../store";
import EAPageHeader from "../shared/EAPageHeader";
import EAPageMessage from "../shared/EAPageMessage";
import EAAlert from "../shared/EAAlert";
import EACalendarModal from "../shared/CalendarModal/EACalendarModal";
import ScheduleBar from "../shared/ScheduleBar";
import WeekHourGrid from "../shared/WeekHourGrid";
import { checkComponentActive } from "../../../utils/checkComponentActive";
import getId from "../../../utils/getId";
import "./style.scss";

interface IProps {
  embedded?: boolean;
  hideFilters?: boolean;
  defaultExerciseGroup?: string;
  defaultZone?: string;
  defaultInstructor?: string;
  dateFromValue?: string;
  dateToValue?: string;
}

const SCHEDULE_BREAKPOINT = "(max-width: 980px)";

const ScheduleExerise: React.FC<IProps> = ({
  embedded = false,
  hideFilters = false,
  defaultExerciseGroup,
  defaultZone,
  defaultInstructor,
  dateFromValue,
  dateToValue,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(SCHEDULE_BREAKPOINT);
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const initialFilters = useMemo(
    () => buildScheduleFilters(defaultExerciseGroup, defaultZone, defaultInstructor),
    [defaultExerciseGroup, defaultZone, defaultInstructor],
  );
  const {
    nextWeek,
    previousWeek,
    canGoPreviousWeek,
    canGoNextWeek,
    minDate,
    maxDate,
    defaultFilters,
    dateToState,
    dateFromState,
    getFirstAllExercises,
    exercisesList,
    loading,
    refreshExercisesList,
    filters,
    setFilters,
    selectDesktopDate,
  } = useSchedule({ dateFromValue, dateToValue, initialFilters });

  const { handleResetFilters } = useAdditionalHooks(filters, setFilters, defaultFilters);
  const week = useFitnessWeekView(dateFromState, exercisesList, minDate, maxDate);

  useEffect(() => {
    setSelectedDay((current) => {
      const from = startOfDay(dateFromState);
      const to = startOfDay(dateToState);
      const inWeek = (date: Date) => {
        const time = startOfDay(date).getTime();
        return time >= from.getTime() && time <= to.getTime();
      };
      let next = startOfDay(current);
      if (!inWeek(next)) {
        const today = startOfDay(new Date());
        next = inWeek(today) ? today : from;
      }
      if (minDate && next.getTime() < startOfDay(minDate).getTime()) next = startOfDay(minDate);
      if (maxDate && next.getTime() > startOfDay(maxDate).getTime()) next = startOfDay(maxDate);
      if (!inWeek(next)) next = from;
      if (next.getTime() === startOfDay(current).getTime()) return current;
      return next;
    });
  }, [dateFromState, dateToState, minDate, maxDate]);

  const canShiftDay = (delta: number) => {
    const next = startOfDay(addDays(selectedDay, delta));
    if (delta < 0 && minDate && next.getTime() < startOfDay(minDate).getTime()) return false;
    if (delta > 0 && maxDate && next.getTime() > startOfDay(maxDate).getTime()) return false;
    return true;
  };

  const shiftDay = (delta: -1 | 1) => {
    if (!canShiftDay(delta)) return;
    const next = startOfDay(addDays(selectedDay, delta));
    setSelectedDay(next);
    const nextWeekStart = startOfWeek(next, { weekStartsOn: 1 });
    if (nextWeekStart.getTime() !== startOfDay(dateFromState).getTime()) {
      selectDesktopDate(next);
    }
  };

  useEffect(() => {
    getFirstAllExercises();
    //eslint-disable-next-line
  }, []);

  const activeFilterCount = [
    getId(filters.exerciseGroups),
    getId(filters.zones),
    getId(filters.humans),
    filters.onlyWithFreeSpots ? "free" : "",
  ].filter(Boolean).length;

  if (!embedded && checkComponentActive(saleConfiguration.componentList, "Calendar") === false) {
    return <EAAlert message={t("Nie znaleziono oferty")} type="error" />;
  }

  return (
    <div className={embedded ? undefined : "container"}>
      {!embedded && <EAPageHeader componentId="Calendar" />}
      {!embedded && <EAPageMessage message={saleConfiguration.message_fitnessExercise} />}

      <ScheduleBar
        hideFilters={hideFilters}
        activeFilterCount={activeFilterCount}
        onOpenFilters={() => setFiltersOpen(true)}
        onClearFilters={handleResetFilters}
        dateLabel={
          isMobile
            ? format(selectedDay, "dd.MM.yyyy")
            : `${format(dateFromState, "dd.MM")} - ${format(dateToState, "dd.MM.yyyy")}`
        }
        onPrevious={isMobile ? () => shiftDay(-1) : previousWeek}
        onNext={isMobile ? () => shiftDay(1) : nextWeek}
        canGoPrevious={isMobile ? canShiftDay(-1) : canGoPreviousWeek}
        canGoNext={isMobile ? canShiftDay(1) : canGoNextWeek}
        onOpenCalendar={() => setCalendarOpen(true)}
        previousTitle={isMobile ? t("poprzedni_dzien", "Poprzedni dzień") : t("poprzedni_tydzien")}
        nextTitle={isMobile ? t("nastepny_dzien", "Następny dzień") : t("nastepny_tydzien")}
        calendarOnlyOnMobile
      />

      <WeekHourGrid
        columns={week.columns}
        hours={week.allHours}
        isPastHour={week.isPastHour}
        activeColumn={format(selectedDay, "yyyy-MM-dd")}
        onShiftColumn={shiftDay}
        canShiftPrevious={canShiftDay(-1)}
        canShiftNext={canShiftDay(1)}
        loading={loading}
        isEmpty={!loading && week.isEmpty}
        isColumnInRange={week.isColumnInRange}
        empty={<p>{t("brak_wydarzen_dla_tego_tygodnia")}</p>}
        occupiesSlot={(dayKey, hour) =>
          week.exercisesForColumn(dayKey).some((exercise) => exerciseHourKey(exercise) === hour)
        }
        renderCell={(dayKey, hour) =>
          week
            .exercisesForColumn(dayKey)
            .filter((exercise) => exerciseHourKey(exercise) === hour)
            .map((exercise) => (
              <SingleExercise
                key={`${exercise.objectDb}-${exercise.objectId}`}
                exercise={exercise}
                onRefreshExercises={() => refreshExercisesList()}
              />
            ))
        }
      />

      {filtersOpen && (
        <ExerciseFltersModal
          open={filtersOpen}
          setOpen={setFiltersOpen}
          filters={filters}
          setFilters={setFilters}
          dateFromState={dateFromState}
          dateToState={dateToState}
        />
      )}

      {calendarOpen && (
        <EACalendarModal
          onClose={() => setCalendarOpen(false)}
          onDatePick={(date) => {
            const day = startOfDay(date);
            setSelectedDay(day);
            selectDesktopDate(day);
            setCalendarOpen(false);
          }}
          date={isMobile ? selectedDay : dateFromState}
          title={isMobile ? t("wybor_dnia", "Wybór dnia") : t("wybor_tygodnia", "Wybór tygodnia")}
          fromDate={minDate ?? new Date()}
          toDate={maxDate ?? addYears(new Date(), 1)}
        />
      )}
    </div>
  );
};

export default ScheduleExerise;
